/** Maximum time (ms) to wait for the scroll to settle before resolving. */
const SCROLL_SETTLE_TIMEOUT_MS = 700;

/**
 * Smooth-scrolls the window to `top` (clamped to ≥ 0) and resolves once the
 * scroll settles.
 *
 * Settle is detected via the native `scrollend` event (one-shot listener) with
 * a {@link SCROLL_SETTLE_TIMEOUT_MS} fallback so the promise always resolves:
 * - immediately (via early return) when running outside a browser
 * - on `scrollend` in browsers that support it
 * - after the timeout otherwise (already at target, or unsupported browser)
 *
 * @example
 * await smoothScrollWindowTo(500);
 * // scroll has settled — safe to re-enable scroll-spy
 */
export async function smoothScrollWindowTo(top: number): Promise<void> {
  const win = globalThis.window as Window | undefined;
  if (win === undefined) {
    return;
  }
  return new Promise<void>((resolve) => {
    let settled = false;
    // Use a ref so `settle` can clear the timeout without a forward-declaration issue.
    const ref: { timeoutId: ReturnType<typeof setTimeout> | undefined } = {
      timeoutId: undefined,
    };

    const settle = () => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(ref.timeoutId);
      resolve();
    };

    // { once: true } auto-removes the listener on first fire — no manual cleanup needed.
    win.addEventListener('scrollend', settle, { once: true });
    ref.timeoutId = setTimeout(settle, SCROLL_SETTLE_TIMEOUT_MS);
    win.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  });
}
